import {getEnv} from '@config/getEnv';
import {useContainerSize} from '@hooks';
import classNames from 'classnames';
import {MouseEventHandler, useEffect, useState} from 'react';
import {useVideoPlayer, useVideoPlayerState} from '..';
import VideoNotStartedPlayButton from '../NotStartedPlayButton/VideoNotStartedPlayButton';
import VideoPlayerProgressSlider from '../ProgressSlider/VideoPlayerProgressSlider';
import VideoUnmuteButton from '../UnmuteButton/VideoUnmuteButton';
import {useIdle} from './@hooks/useIdle';
import {VideoCCButton} from './CCButton/VideoCCButton';
import {VideoChapterTitle} from './ChapterTitle/VideoChapterTitle';
import {VideoFullscreenButton} from './FullscreenButton/VideoFullscreenButton';
import {VideoPictureInPictureButton} from './PictureInPictureButton/VideoPictureInPictureButton';
import {VideoPlayButton} from './PlayButton/VideoPlayButton';
import {VideoPlayButtonCenter} from './PlayButtonCenter/VideoPlayButtonCenter';
import {VideoPlaybackRateButton} from './PlaybackRateButton/VideoPlaybackRateButton';
import {VideoSkipButton} from './SkipButton/VideoSkipButton';
import {VideoTimeButton} from './TimeButton/VideoTimeButton';
import styles from './VideoPlayerControls.module.css';
import {VideoVolumeButton} from './VolumeButton/VideoVolumeButton';

type IProps = {
    aspectRatio: '9:16' | '16:9';
    isLive?: boolean;
    isLiveRecording?: boolean;
    hideUnmuteButton?: boolean;
};

export const VideoPlayerControls = ({
    aspectRatio,
    isLive,
    isLiveRecording,
    hideUnmuteButton = false,
}: IProps) => {
    const [isVolumeSliderOpen, setIsVolumeSliderOpen] = useState(false);
    const [isHoveringPlayer, setIsHoveringPlayer] = useState(true);
    const [containerRef, containerSize] = useContainerSize();
    const supportsPipAPI =
        typeof document !== 'undefined' && !!document.pictureInPictureEnabled;

    const isTooSmallForSkipButtons = containerSize.width <= 570;
    const isTooSmallForPip = containerSize.width <= 490;
    const isTooSmallForPlayButton = containerSize.width <= 435;

    const isIdle = useIdle(4000);

    const {playPause, setControlsVisible} = useVideoPlayer();
    const isFullscreen = useVideoPlayerState(s => s.isFullscreen);
    const isInPip = useVideoPlayerState(s => s.isInPip);
    const isShort = aspectRatio === '9:16';

    const isStarted = useVideoPlayerState(s => s.isStarted);
    const areControlsVisible = useVideoPlayerState(s => s.areControlsVisible);

    useEffect(() => {
        const controlsAreHidden = isIdle || !isHoveringPlayer;
        setControlsVisible(!controlsAreHidden && !isInPip);
    }, [isHoveringPlayer, isIdle, isInPip, setControlsVisible]);

    const handlePausePlay: MouseEventHandler<HTMLDivElement> = e => {
        e.stopPropagation();
        playPause();
    };

    const showPlayButton = !isTooSmallForPlayButton;
    const showSkipButton =
        !isTooSmallForSkipButtons && !isShort && !isLive && !isLiveRecording;
    const showChapterTitle =
        getEnv('NEXT_PUBLIC_FEATURE_CHAPTER_MARKS') === 'true' &&
        (isLive || isLiveRecording) &&
        isFullscreen;
    const showPictureInPictureButton =
        !isShort && supportsPipAPI && !isTooSmallForPip;
    const showTimeButton = !isShort && !isLive;
    const showCCButton = true;
    const showNotStartedPlayButton = !isStarted;
    const showPlaybackRateButton = !isLive;

    if (showNotStartedPlayButton) {
        return <VideoNotStartedPlayButton />;
    }

    if (isInPip) {
        return null;
    }

    return (
        <>
            {!hideUnmuteButton && <VideoUnmuteButton />}
            <div
                ref={containerRef}
                className={classNames(styles.container, {
                    [styles.controlsAreHidden]: !areControlsVisible,
                })}
                onClick={handlePausePlay}
                onMouseEnter={() => setIsHoveringPlayer(true)}
                onMouseLeave={() => setIsHoveringPlayer(false)}
            >
                <VideoPlayButtonCenter />
                <VideoPlayerProgressSlider />
                {!(isShort && isFullscreen) && (
                    <div className={styles.overlayContainer}>
                        <div
                            className={styles.leftSide}
                            onMouseLeave={() => setIsVolumeSliderOpen(false)}
                        >
                            {showPlayButton && <VideoPlayButton />}
                            <VideoVolumeButton
                                setIsVolumeSliderOpen={setIsVolumeSliderOpen}
                                isVolumeSliderOpen={isVolumeSliderOpen}
                            />
                            {showTimeButton && <VideoTimeButton />}
                            {showSkipButton && <VideoSkipButton />}
                            {showChapterTitle && <VideoChapterTitle />}
                        </div>
                        <div className={styles.rightSide}>
                            {showCCButton && <VideoCCButton />}
                            {showPlaybackRateButton && (
                                <VideoPlaybackRateButton />
                            )}
                            {showPictureInPictureButton && (
                                <VideoPictureInPictureButton />
                            )}
                            <VideoFullscreenButton />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
